<template>
  <div>
    <h3>Payer group {{groupYearMonth}}</h3>

    <template v-if="payerGroup !== null">
      <table class="prop-table mb-1">
        <tr>
          <th>
            Status
          </th>
          <td>
            {{payerGroup.inited | getMonthPayerBillingGroupPreviewsStatusStr}}
          </td>
        </tr>        
      </table>

      <div>
        <b-button
          v-if="isInitButtonVisible"
          @click="initPayerGroup"
        >
          Init
        </b-button>

        <b-button
          v-if="isImportButtonVisible"
          @click="importPayerGroupToClickHouse"
        >
          Import to ClickHouse
        </b-button>
      </div>

      <template v-if="payerGroup.inited">
        <h4 class="mt-4">Payers</h4>
        <ol v-if="hasPayers">
          <li
            v-for="payerId in payerIds"
            :key="payerId"
          >
            <player-link :playerId="payerId" />
          </li>
        </ol>
        <p v-if="!hasPayers">
          No payers.
        </p>
      </template>

    </template>

    <loading-indicator
      v-if="isLoading"
    />
  </div>
</template>

<script>
import auth from '../auth.js';

import { HTTP } from '../http-common.js';
import notifier from '../notifier.js';

import { getMonthPayerBillingGroupPreviewsStatusStr } from '../model/billing_stat.js';
import { isRoleAdmin } from '../util.js';

import LoadingIndicator from './LoadingIndicator.vue';
import PlayerLink from './PlayerLink.vue';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'MonthPayerGroup',

  components: {
    LoadingIndicator,
    PlayerLink,
  },

  props: {
    groupYearMonth: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      actor: auth.getViewer(),

      loadState: LOAD_STATE_INIT,
      payerGroup: null,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    hasPayers() {
      const payerGroup = this.payerGroup;
      if(payerGroup === null) {
        return false;
      }
      if(!('payerIds' in payerGroup)) {
        return false;
      }

      const ids = payerGroup.payerIds;
      if(ids === null) {
        return false;
      }
      return ids.length;
    },

    /** @returns {?number[]} */
    payerIds() {
      const payerGroup = this.payerGroup;
      return payerGroup.payerIds;
    },

    /** @returns {boolean} */
    isInitButtonVisible() {
      if(!this.mayManage) {
        return false
      }

      const payerGroup = this.payerGroup;
      if(payerGroup === null) {
        return false;
      }

      return !payerGroup.inited;
    },

    /** @returns {boolean} */
    isImportButtonVisible() {
      if(!this.mayManage) {
        return false
      }

      const payerGroup = this.payerGroup;
      if(payerGroup === null) {
        // Data not loaded.
        return false;
      }

      return payerGroup.inited;
    },

    /** @returns {boolean} */
    mayManage() {
      const actor = this.actor;
      if(actor === null) {
        throw new Error('actor === null');
      }

      return isRoleAdmin(actor.role);
    },
  },

  created() {
    this.loadPayerGroup();
  },

  methods: {
    loadPayerGroup() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      const url = '/stat/month_payer_group/' + this.groupYearMonth;
      HTTP.get(url)
      .then(response => {
        this.payerGroup = response.data.data;
        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(err => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    initPayerGroup() {
      notifier.deleteSenderNotifications(this);

      const payerGroup = this.payerGroup;
      if(payerGroup === null) {
        notifier.showError('Payer group in not loaded.', {sender: this});
        return;
      }
      
      const url = `/stat/month_payer_group/${this.groupYearMonth}/init`;
      HTTP.post(url)
      .then(response => {
        const data = response.data.data;
        payerGroup.payerIds = data;
        payerGroup.inited = true;
        notifier.showSuccess('Payer group inited.');
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },

    importPayerGroupToClickHouse() {
      notifier.deleteSenderNotifications(this);

      const url = `/stat/month_payer_group/${this.groupYearMonth}/import_to_clickhouse`;
      HTTP.post(url)
      .then(() => {
        notifier.showSuccess('Data imported to ClickHouse.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },
  },

  filters: {
    getMonthPayerBillingGroupPreviewsStatusStr,
  }
};
</script>
